import {
  CandidateProcedure,
  CandidateProcedureDecision,
  CandidateProcedureStatus,
  CandidateVerification,
  ManagerOfCompany,
  Pagination,
} from '@interfaces';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as managerActions from './manager.actions';
import { ManagerState, initialState } from './manager.state';

function updateProcedureDecisionInList(
  procedures: CandidateProcedure[] | undefined,
  procedureId: string,
  decision: CandidateProcedureDecision
): CandidateProcedure[] | undefined {
  if (!procedures?.length) {
    return procedures;
  }

  const decisionDate = new Date();

  return procedures.map(procedure => {
    if (procedure.id !== procedureId) {
      return procedure;
    }

    if (decision === 'start') {
      return {
        ...procedure,
        status: CandidateProcedureStatus.CPS_2_0_0__STARTED,
        approvalDate: decisionDate,
        approvedBy: procedure.approvedBy,
        approvedById: procedure.approvedById,
        rejectionDate: null,
        rejectedBy: null,
        rejectedById: null,
      };
    }

    return {
      ...procedure,
      status: CandidateProcedureStatus.CPS_3_0_1__REJECTED,
      approvalDate: null,
      approvedBy: null,
      approvedById: null,
      rejectionDate: decisionDate,
    };
  });
}

function updateProcedureDecisionOnVerification(
  verification: Partial<CandidateVerification> | null,
  procedureId: string,
  decision: CandidateProcedureDecision
): Partial<CandidateVerification> | null {
  if (!verification) {
    return verification;
  }

  return {
    ...verification,
    updatedAt: new Date(),
    proceduresBeforeHire: updateProcedureDecisionInList(verification.proceduresBeforeHire, procedureId, decision),
  };
}

function mergeProceduresInList(
  procedures: CandidateProcedure[] | undefined,
  addedProcedures: CandidateProcedure[]
): CandidateProcedure[] {
  if (!procedures?.length) {
    return [...addedProcedures];
  }

  const addedByType = new Map(addedProcedures.map(procedure => [procedure.type, procedure]));
  const existingTypes = new Set(procedures.map(procedure => procedure.type));

  return [
    ...procedures.map(procedure => addedByType.get(procedure.type) ?? procedure),
    ...addedProcedures.filter(procedure => !existingTypes.has(procedure.type)),
  ];
}

function mergeAddedProceduresOnVerification(
  verification: Partial<CandidateVerification> | null,
  addedProcedures: CandidateProcedure[]
): Partial<CandidateVerification> | null {
  if (!verification) {
    return verification;
  }

  return {
    ...verification,
    updatedAt: new Date(),
    proceduresBeforeHire: mergeProceduresInList(verification.proceduresBeforeHire, addedProcedures),
  };
}

const reducer: ActionReducer<ManagerState, Action> = createReducer(
  initialState,
  on(
    managerActions.getMyManagedCompanies,
    (state): ManagerState => ({
      ...state,
      loadingManagedCompanies: true,
    })
  ),
  on(
    managerActions.getMyManagedCompaniesSuccess,
    (state, { managedCompanies }): ManagerState => ({
      ...state,
      loadingManagedCompanies: false,
      managedCompanies,
    })
  ),
  on(
    managerActions.getMyManagedCompaniesError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingManagedCompanies: false,
      errorMessage,
    })
  ),

  on(
    managerActions.searchProcessesOfManagedEmployees,
    (state): ManagerState => ({
      ...state,
      loadingProcesses: true,
    })
  ),
  on(
    managerActions.searchProcessesOfManagedEmployeesSuccess,
    (state, { processes }): ManagerState => ({
      ...state,
      loadingProcesses: false,
      processes: processes.data,
      processesPagination: processes.pagination,
    })
  ),
  on(
    managerActions.searchProcessesOfManagedEmployeesError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingProcesses: false,
      errorMessage,
    })
  ),

  on(
    managerActions.searchCandidatesVerifications,
    (state): ManagerState => ({
      ...state,
      loadingCandidateVerifications: true,
    })
  ),
  on(
    managerActions.searchCandidatesVerificationsSuccess,
    (state, { candidateVerification }): ManagerState => ({
      ...state,
      loadingCandidateVerifications: false,
      candidateVerifications: candidateVerification.data,
      candidateVerificationsPagination: candidateVerification.pagination,
    })
  ),
  on(
    managerActions.searchCandidatesVerificationsError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingCandidateVerifications: false,
      errorMessage,
    })
  ),

  on(
    managerActions.getCandidateVerificationDetails,
    (state): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
      candidateVerificationDetails: null,
    })
  ),
  on(
    managerActions.getCandidateVerificationDetailsSuccess,
    (state, { candidateVerification }): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerificationDetails: candidateVerification,
    })
  ),
  on(
    managerActions.getCandidateVerificationDetailsError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    managerActions.updateCandidateVerificationBasicDetails,
    (state): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
    })
  ),
  on(
    managerActions.updateCandidateVerificationBasicDetailsSuccess,
    (state, { candidateVerification }): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              companyId: candidateVerification.companyId,
              candidateDetails: {
                ...item.candidateDetails,
                ...candidateVerification.candidateDetails,
              },
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
    })
  ),
  on(
    managerActions.updateCandidateVerificationBasicDetailsError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    managerActions.regenerateCandidateFormLinkAndPin,
    (state): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
    })
  ),
  on(
    managerActions.regenerateCandidateFormLinkAndPinSuccess,
    (state, { candidateVerification }): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              companyId: candidateVerification.companyId,
              status: candidateVerification.status,
              result: candidateVerification.result,
              candidateDetails: {
                ...item.candidateDetails,
                ...candidateVerification.candidateDetails,
              },
              candidateDetailsForm: {
                ...item.candidateDetailsForm,
                ...candidateVerification.candidateDetailsForm,
              },
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
    })
  ),
  on(
    managerActions.regenerateCandidateFormLinkAndPinError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    managerActions.setCandidateVerificationDecision,
    (state): ManagerState => ({
      ...state,
      errorMessage: null,
    })
  ),
  on(
    managerActions.setCandidateVerificationDecisionSuccess,
    (state, { candidateVerification }): ManagerState => ({
      ...state,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status,
              result: candidateVerification.result,
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    managerActions.setCandidateVerificationDecisionError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      errorMessage,
    })
  ),

  on(
    managerActions.updateCandidateVerificationProcedureDecision,
    (state): ManagerState => ({
      ...state,
      loadingCandidateVerificationProcedureDecision: true,
    })
  ),
  on(
    managerActions.updateCandidateVerificationProcedureDecisionSuccess,
    (state, { procedureId, decision }): ManagerState => ({
      ...state,
      loadingCandidateVerificationProcedureDecision: false,
      candidateVerificationDetails: updateProcedureDecisionOnVerification(
        state.candidateVerificationDetails,
        procedureId,
        decision
      ),
      candidateVerifications: state.candidateVerifications.map(item => {
        if (item.id !== state.candidateVerificationDetails?.id) {
          return item;
        }

        return updateProcedureDecisionOnVerification(item, procedureId, decision) ?? item;
      }),
    })
  ),
  on(
    managerActions.updateCandidateVerificationProcedureDecisionError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingCandidateVerificationProcedureDecision: false,
      errorMessage,
    })
  ),

  on(
    managerActions.addCandidateOptionalServicesSuccess,
    (state, { candidateVerificationId, addedServices }): ManagerState => ({
      ...state,
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerificationId
          ? mergeAddedProceduresOnVerification(state.candidateVerificationDetails, addedServices)
          : state.candidateVerificationDetails,
      candidateVerifications: state.candidateVerifications.map(item => {
        if (item.id !== candidateVerificationId) {
          return item;
        }

        return mergeAddedProceduresOnVerification(item, addedServices) ?? item;
      }),
    })
  ),
  on(
    managerActions.addCandidateOptionalServicesError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      errorMessage,
    })
  ),

  on(
    managerActions.getCandidateVerificationHistory,
    (state): ManagerState => ({
      ...state,
      candidateVerificationHistory: null,
      loadingCandidateVerificationHistory: true,
    })
  ),
  on(
    managerActions.getCandidateVerificationHistorySuccess,
    (state, { candidateVerificationHistory }): ManagerState => ({
      ...state,
      candidateVerificationHistory,
      loadingCandidateVerificationHistory: false,
    })
  ),
  on(
    managerActions.getCandidateVerificationHistoryError,
    (state, { errorMessage }): ManagerState => ({
      ...state,
      loadingCandidateVerificationHistory: false,
      errorMessage,
    })
  )
);

export function managerReducer(state: ManagerState | undefined, action: Action): ManagerState {
  return reducer(state, action);
}
